import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import type { Permission } from '@/types';
import { Check, ArrowLeft, Shield, Info, Trash, Key } from 'lucide-react';
import { usePermissions } from '@/store/permission';

import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { InputField } from '@/components/ui/InputField';
import { useLayout } from '@/contexts/layout-context';
import { Loader } from '@/components/loader';

export const ManagePermissionPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { setBreadcrumbs } = useLayout();

    const getPermission = usePermissions((state) => state.get);
    const updatePermission = usePermissions((state) => state.update);
    const deletePermission = usePermissions((state) => state.delete);

    const [perm, setPerm] = useState<Permission | null>(null);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // Delete state
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        if (!id) return;
        getPermission(id).then(p => {
            setPerm(p);
            setBreadcrumbs([{ name: 'Permissions', to: '/m/permissions' }, { name: p.name || 'Edit Permission' }]);
        }).catch(() => toast.error('Failed to load permission'))
            .finally(() => setLoading(false));
    }, [id, getPermission, setBreadcrumbs]);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!perm || !id) return;
        setIsSaving(true);
        try {
            await updatePermission(id, {
                name: perm.name,
                description: perm.description || '',
            });
            toast.success('Permission updated');
        } catch {
            toast.error('Failed to save changes');
        } finally {
            setIsSaving(false);
        }
    };

    const confirmDeletion = async () => {
        if (confirmText !== perm?.name) return;
        setIsDeleting(true);
        try {
            await deletePermission(id!);
            toast.success('Permission deleted');
            navigate('/m/permissions');
        } catch {
            toast.error('Failed to delete permission');
        } finally {
            setIsDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    if (loading || !perm) return <div className="h-full flex items-center justify-center"><Loader /></div>;

    return (
        <div className="p-4 lg:p-8 max-w-4xl mx-auto w-full animate-in fade-in duration-500">
            <PageHeader
                title={perm.name}
                subtitle={`Managing permission: ${perm.resource_kind_id || 'None'}`}
                actions={
                    <Button variant="ghost" onClick={() => navigate('/m/permissions')} className="gap-2">
                        <ArrowLeft className="w-4 h-4" />
                        Back
                    </Button>
                }
            />

            <div className="mt-8 animate-in slide-in-from-bottom-4 duration-500">
                <Card title="Permission Configuration" subtitle="Configure the display name and description for this entitlement." className="glass">
                    <form onSubmit={submit} className="space-y-8">
                        <div className="space-y-6">
                            {/* Read-only Permission ID */}
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Permission ID / Key
                                </label>
                                <div className="flex rounded-lg border border-slate-100 dark:border-slate-800/50 bg-slate-50/50 dark:bg-slate-950/20 overflow-hidden">
                                    <div className="relative flex-1 flex items-center">
                                        <div className="absolute left-3 text-slate-400">
                                            <Key className="w-4 h-4" />
                                        </div>
                                        <input
                                            type="text"
                                            className="w-full pl-10 pr-4 py-2 bg-transparent text-sm outline-none text-slate-400 dark:text-slate-500 font-mono cursor-not-allowed"
                                            value={perm.id}
                                            disabled
                                        />
                                    </div>
                                </div>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                    Permission ID is immutable after creation. To change the identifier, you must delete and recreate the permission.
                                </p>
                            </div>

                            {/* Editable Permission Name */}
                            <InputField
                                label="Permission Name"
                                value={perm.name}
                                onChange={(e) => setPerm({ ...perm, name: e.target.value })}
                                required
                                icon={<Shield className="w-4 h-4" />}
                            />

                            {/* Editable Description */}
                            <InputField
                                label="Brief Description"
                                value={perm.description || ''}
                                onChange={(e) => setPerm({ ...perm, description: e.target.value })}
                                icon={<Info className="w-4 h-4" />}
                            />

                            {/* Read-only Resource Kind */}
                            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800">
                                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Resource Kind</label>
                                <span className="text-sm font-mono font-bold text-slate-900 dark:text-white bg-white dark:bg-slate-800 px-3 py-1 rounded-lg border border-slate-200 dark:border-slate-700 inline-block mt-1">
                                    {perm.resource_kind_id || 'None'}
                                </span>
                                <p className="text-[10px] text-slate-500 mt-2">Resource Kind is immutable after creation to maintain integrity.</p>
                            </div>
                        </div>

                        <div className="pt-6 border-t border-slate-100 dark:border-slate-800 flex justify-end">
                            <Button type="submit" isLoading={isSaving} className="min-w-40 gap-2">
                                <Check className="w-4 h-4" />
                                Save Changes
                            </Button>
                        </div>
                    </form>
                </Card>

                <Card className="mt-6 border-red-100 dark:border-red-900/20 bg-red-50/10 glass" title="Danger Zone">
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="text-sm font-bold text-slate-900 dark:text-white">Delete Permission</h4>
                            <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1">Permanently remove this permission from the system. This action is irreversible.</p>
                        </div>
                        <Button variant="danger" size="sm" onClick={() => { setConfirmText(''); setShowDeleteConfirm(true); }} className="gap-2">
                            <Trash className="w-4 h-4" />
                            Delete
                        </Button>
                    </div>
                </Card>
            </div>

            {/* ─── Delete confirm modal ─── */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-slate-900/50 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
                    <Card className="w-full max-w-md shadow-2xl border-red-200 dark:border-red-900/30">
                        <div className="flex flex-col items-center text-center py-4">
                            <div className="w-12 h-12 bg-red-100 dark:bg-red-500/10 text-red-600 rounded-full flex items-center justify-center mb-4">
                                <Trash className="w-6 h-6" />
                            </div>
                            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Delete Permission</h2>
                            <p className="text-sm text-slate-500 mt-2 px-4">
                                This will permanently remove <span className="font-bold text-slate-900 dark:text-white">{perm.name}</span>.
                                All associated access rights will be revoked immediately.
                            </p>

                            <div className="w-full mt-6 text-left space-y-3">
                                <InputField
                                    label={`Type "${perm.name}" to confirm`}
                                    value={confirmText}
                                    onChange={(e) => setConfirmText(e.target.value)}
                                    placeholder={perm.name}
                                />
                            </div>

                            <div className="w-full flex gap-3 mt-8">
                                <Button variant="ghost" className="flex-1" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
                                <Button
                                    variant="danger"
                                    className="flex-1"
                                    disabled={confirmText !== perm.name || isDeleting}
                                    isLoading={isDeleting}
                                    onClick={confirmDeletion}
                                >
                                    Confirm Delete
                                </Button>
                            </div>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
};
